import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import { IntlProvider } from "react-intl";
import Cookies from "js-cookie";
import zh_CN from "@/locales/zh_CN";
import en_US from "@/locales/en_US";
import ar from "@/locales/ar";
import de from "@/locales/de";
import es from "@/locales/es";
import fr from "@/locales/fr";
import it from "@/locales/it";
import ja from "@/locales/ja";
import ko from "@/locales/ko";
import pt from "@/locales/pt";
import ru from "@/locales/ru";
import zh_tw from "@/locales/zh_tw";

// 定义语言类型
type Locale =
  | "zh-cn"
  | "en"
  | "ar"
  | "de"
  | "es"
  | "fr"
  | "it"
  | "ja"
  | "ko"
  | "pt"
  | "ru"
  | "zh-tw";

// 定义语言包类型
type Messages = Record<string, string>;

// 语言包映射
const messages: Record<Locale, Messages> = {
  "zh-cn": zh_CN,
  en: en_US,
  ar: ar,
  de: de,
  es: es,
  fr: fr,
  it: it,
  ja: ja,
  ko: ko,
  pt: pt,
  ru: ru,
  "zh-tw": zh_tw,
};

// RTL 语言列表
const RTL_LOCALES: Locale[] = ["ar"];

// 默认语言
const DEFAULT_LOCALE: Locale = "en";

// 创建上下文
interface I18nContextProps {
  locale: Locale;
  isRtl: boolean;
  setLocale: (locale: Locale) => void;
  formatMessage: (id: any, values?: Record<string, any>) => string;
}

const I18nContext = createContext<I18nContextProps>({
  locale: DEFAULT_LOCALE,
  isRtl: false,
  setLocale: () => {},
  formatMessage: () => "",
});

// 全局变量用于存储国际化实例
let globalI18n: I18nContextProps | null = null;

// 提供者组件
interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>(() => {
    // 从 cookie 读取保存的语言设置
    const savedLocale = Cookies.get("locale") as Locale | null;
    return savedLocale || DEFAULT_LOCALE;
  });

  // 当前语言是否为 RTL
  const isRtl = RTL_LOCALES.includes(locale);

  // 当语言改变时，保存到 cookie 中
  useEffect(() => {
    Cookies.set("locale", locale, { expires: 30 });
  }, [locale]);

  // 当语言改变时，自动设置根容器的 dir 属性
  useEffect(() => {
    const direction = isRtl ? "rtl" : "ltr";
    // 嵌入模式根容器
    const aiRoot = document.getElementById("ai-customer-service");
    if (aiRoot) {
      aiRoot.setAttribute("dir", direction);
    }
    // 临时聊天模式根容器
    const tempChatRoot = document.getElementById("temp-chat-root");
    if (tempChatRoot) {
      tempChatRoot.setAttribute("dir", direction);
    }
  }, [isRtl]);

  // 设置全局国际化实例
  const i18nInstance = useMemo(() => {
    // 格式化消息的辅助函数
    const formatMessage = (id: any, values?: Record<string, any>) => {
      const message = messages[locale]
        ? messages[locale]?.[id]
        : messages[DEFAULT_LOCALE][id] || id;

      if (!values) return message;

      // 简单替换占位符
      return Object.keys(values).reduce((msg, key) => {
        return msg.replace(new RegExp(`{${key}}`, "g"), values[key]);
      }, message);
    };
    return { locale, isRtl, setLocale, formatMessage };
  }, [locale, isRtl]);

  useEffect(() => {
    globalI18n = i18nInstance;
  }, [i18nInstance]);

  return (
    <I18nContext.Provider value={i18nInstance}>
      <IntlProvider
        locale={locale || DEFAULT_LOCALE}
        messages={messages[locale] || messages[DEFAULT_LOCALE]}
        defaultLocale={DEFAULT_LOCALE}
        onError={(err) => {
          // 自定义错误处理
          if (err.code === "MISSING_DATA") {
            console.warn(`Missing translation: ${err.message}`);
          } else {
            console.error(`Intl error: ${err.message}`);
          }
        }}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
};

// 自定义hook
export const useI18n = () => useContext(I18nContext);

// 导出获取全局国际化实例的函数
export const getGlobalI18n = () => {
  if (!globalI18n) {
    return {
      formatMessage: (id: string, defaultMessage?: string) =>
        defaultMessage || id,
    };
  }
  return globalI18n;
};
